import React, { useEffect, useMemo, useState } from 'react';

function MediaUploader({ files, onFilesChange }) {
  const [error, setError] = useState('');
  const [durations, setDurations] = useState({});
  const maxFiles = 5;

  const acceptedTypes = useMemo(() => ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime'], []);

  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [files]);

  const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024;

    if (!acceptedTypes.includes(file.type)) {
      setError('Only JPG, PNG, WEBP, MP4, and MOV files are allowed.');
      return false;
    }

    if (file.size > maxSize) {
      setError('File size exceeds 10 MB. Please choose a smaller file.');
      return false;
    }

    return true;
  };

  const handleFiles = (event) => {
    setError('');
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;

    const availableSlots = Math.max(maxFiles - files.length, 0);
    if (availableSlots <= 0) {
      setError('You can upload up to 5 files per review.');
      event.target.value = '';
      return;
    }

    const uploaded = Array.from(event.target.files).filter(validateFile).map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    onFilesChange([...files, ...uploaded].slice(0, maxFiles));
    if (uploaded.length > availableSlots) {
      setError('Only the first 5 files were kept.');
    }
    event.target.value = '';
  };

  const removeFile = (index) => {
    setError('');
    onFilesChange(files.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-slate-700">Upload media <span className="text-slate-500">(OPTIONAL)</span></label>
      <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-900">Images and videos</p>
            <p className="text-sm text-slate-500">JPG, PNG, WEBP, MP4, MOV. Max 10MB per file. Up to 5 files.</p>
          </div>
          <label className="inline-flex cursor-pointer rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800">
            Browse
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,.mov"
              multiple
              onChange={handleFiles}
              className="sr-only"
            />
          </label>
        </div>
      </div>

      {error && (
        <p className="text-sm text-rose-600">{error}</p>
      )}

      {files.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {files.map((item, index) => (
            <div key={item.preview} className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white">
              {item.file.type.startsWith('video/') ? (
                <>
                  <video
                    src={item.preview}
                    controls
                    onLoadedMetadata={(e) => {
                      const duration = e.currentTarget.duration;
                      if (!Number.isFinite(duration)) return;
                      setDurations((prev) => ({ ...prev, [item.preview]: duration }));
                    }}
                    className="h-44 w-full object-cover"
                  />
                  <span className="absolute bottom-3 right-3 rounded-full bg-black/65 px-2 py-0.5 text-xs font-semibold text-white">
                    {formatDuration(durations[item.preview])}
                  </span>
                </>
              ) : (
                <img src={item.preview} alt={item.file.name} className="h-44 w-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-slate-700 shadow-sm transition hover:bg-white"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MediaUploader;
